import React from 'react';
import { FilterColumnProps } from '../types';
import { Typography } from '../../../interfaces/typography';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { CustomMetricColumns } from '../../../interfaces/table';
import { CustomMetricValueType } from '../../../interfaces/db';

export function BooleanFilter({ value, onChange }: FilterColumnProps) {
  const customDefinitions = useSelector((state: RootState) => state.customDefinitions.value) ?? [];
  
  // Helper function to check if this is a custom metric column
  const isCustomMetricColumn = (columnName: string): boolean => {
    return Object.values(CustomMetricColumns).includes(columnName as CustomMetricColumns) && 
           columnName !== CustomMetricColumns.SESSION_ID && 
           !columnName.startsWith('text_');
  };

  // Helper function to get custom metric definition
  const getCustomMetricDefinition = (columnName: string) => {
    if (!isCustomMetricColumn(columnName)) return null;
    
    const match = columnName.match(/^metric_(\d+)$/);
    if (!match) return null;
    
    const metricIdx = parseInt(match[1], 10);
    return customDefinitions.find(def => def.metric_idx === metricIdx);
  };

  // Check if this is a custom metric boolean column
  const isCustomMetricBoolean = isCustomMetricColumn(value.column_name) && 
    getCustomMetricDefinition(value.column_name)?.value_type === CustomMetricValueType.BOOLEAN;

  // Convert stored values back to boolean for display
  const getDisplayValue = (storedValue: any): boolean | null => {
    if (storedValue === null || storedValue === undefined) return null;
    
    if (isCustomMetricBoolean) {
      // For custom metrics, convert integers back to booleans for display
      if (storedValue === 1) return true;
      if (storedValue === 0) return false;
      return null;
    }
    
    // For regular boolean columns, use the value as-is
    return storedValue;
  };

  // Convert boolean values to appropriate format for backend
  const getBackendValue = (displayValue: boolean | null): any => {
    if (displayValue === null) return null;
    
    if (isCustomMetricBoolean) {
      // For custom metrics, convert booleans to integers
      return displayValue ? 1 : 0;
    }
    
    // For regular boolean columns, use the value as-is
    return displayValue;
  };

  const currentValue = getDisplayValue(value.value_safe);

  const handleValueChange = (newValue: boolean | null) => {
    if (newValue === null) {
      // "All" selected - remove the filter entirely
      onChange({
        ...value,
        value_safe: null
      });
    } else {
      const backendValue = getBackendValue(newValue);
      onChange({
        ...value,
        value_safe: backendValue
      });
    }
  };

  return (
    <div className="flex w-full bg-gray-50 rounded-md p-0.5 gap-0.5">
      <button
        onClick={() => handleValueChange(true)}
        className={`
          flex-1 px-3 py-1 text-sm rounded transition-colors
          ${currentValue === true 
            ? 'bg-white text-gray-900 shadow-sm' 
            : 'text-gray-600 hover:text-gray-900'
          }
        `}
      >
        Yes
      </button>
      <button
        onClick={() => handleValueChange(false)}
        className={`
          flex-1 px-3 py-1 text-sm rounded transition-colors
          ${currentValue === false 
            ? 'bg-white text-gray-900 shadow-sm' 
            : 'text-gray-600 hover:text-gray-900'
          }
        `}
      >
        No
      </button>
      <button
        onClick={() => handleValueChange(null)}
        className={`
          flex-1 px-3 py-1 text-sm rounded transition-colors
          ${currentValue === null 
            ? 'bg-white text-gray-900 shadow-sm' 
            : 'text-gray-600 hover:text-gray-900'
          }
        `}
      >
        All
      </button>
    </div>
  );
} 